// src/views/QuotesView.js
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { Plus, ArrowUp, ArrowDown, XCircle, AlertTriangle, LayoutGrid, List, Search, Eye, EyeOff } from 'lucide-react'; // EyeOff: estdel-f9ac8ba8
import { useQuoteViews, quoteViewSummary } from '../hooks/useQuoteView'; // qview-6b2d4e91
import { formatCurrency, formatDate } from '../utils/formatters';
import QuoteStatusBadge from '../components/common/QuoteStatusBadge';
import usePipelineStages from '../hooks/usePipelineStages'; // qattn-8c4e1f92-list
import QuotePipelineBoard from '../components/crm/QuotePipelineBoard'; // qboard-7e3c9b42
import { matchesQuoteSearch } from '../utils/jobSearch'; // qboard-7e3c9b42 — reused from Seam 7b
import { canSeeAllCustomers } from '../utils/permissions'; // officecust-9a4d2c61
import DeletedQuotesTable from '../components/crm/DeletedQuotesTable'; // estdel-f9ac8ba8
import {
    resolveDocTerminology, // r1-p1-docterm-qv-import-2d9f4a18
    // qexp-6f2a9d38-list — Seam 5: the ONE derived-expiry rule
    EXPIRY_STATES, resolveExpiryState, resolveExpiryWarningDays,
    // qattn-8c4e1f92-list — Seam 8: the ONE attention rule
    findPipelineStage, resolveQuoteAttention, quoteAttentionLabel,
    // qboard-7e3c9b42 — Seam 8b: the salesperson filter and the card byline
    resolveQuoteSalesPerson,
    isDepositAmountNeeded, // depdraft-3c8e5a14
    isQuoteArchived, // estdel-f9ac8ba8
} from '../utils/quoteSchema';

// qboard-7e3c9b42 — view preference, same treatment as the customer board
// (CustomerManagementView.js:31-41). 'table' is the first-time default on every
// width: an existing user's screen must not change shape under them because a
// board shipped.
const VIEW_MODE_STORAGE_KEY = 'spatchy_quotes_view_mode';
// Below this width the page OPENS on the list even for someone whose saved
// preference is the board — a 13-column horizontal scroll is not what you want
// to land on, on a phone. The board stays one tap away, and the saved
// preference is left alone so their desktop is unaffected.
const BOARD_DEFAULT_MIN_WIDTH = 768;

const SALES_FILTER_ALL = 'all';
const SALES_FILTER_UNASSIGNED = 'unassigned';

const QuotesView = ({ currentUserProfile, onQuoteSelect, openQuoteModal, companySettings, users }) => { // r1-p1-docterm-qv-prop-7b3e1c95; users: qboard-7e3c9b42
    const [quotes, setQuotes] = useState([]);
    // estdel-f9ac8ba8 — deleted (hidden) estimates live apart: never in `quotes` (list, board, search, counts),
    // only in the "Deleted (N)" view. Not persisted, like Needs attention.
    const [deletedQuotes, setDeletedQuotes] = useState([]);
    const [showDeleted, setShowDeleted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
    // qboard-7e3c9b42 — board or list.
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
            if (saved !== 'board' && saved !== 'table') return 'table';
            if (saved === 'board' && typeof window !== 'undefined'
                && window.innerWidth < BOARD_DEFAULT_MIN_WIDTH) return 'table';
            return saved;
        } catch {
            return 'table';
        }
    });
    useEffect(() => {
        try { localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode); } catch { /* private mode */ }
    }, [viewMode]);
    // qboard-7e3c9b42 — search and salesperson are FILTERS on both views. Unlike
    // the calendar (where search is a results list because filtering a grid
    // hides the context you need), a list and a board are both already
    // filtered surfaces, so narrowing them is what people expect.
    const [searchTerm, setSearchTerm] = useState('');
    const [salesFilter, setSalesFilter] = useState(SALES_FILTER_ALL);
    // qattn-8c4e1f92-list — Seam 8. Its own axis, exactly like needsQbAttention
    // on Invoices: it ANDs with the status filter rather than replacing it, and
    // is deliberately NOT persisted — it is a "what needs me right now" glance,
    // not a saved view.
    const [attentionOnly, setAttentionOnly] = useState(false);
    
    // --- DEEP LINKING STATE ---
    const location = useLocation();
    const navigate = useNavigate();
    
    // Get the filter param safely
    const queryParams = new URLSearchParams(location.search);
    const filterParam = queryParams.get('filter');

    // qnotif-6d2f8b47 (Seam 11d) — `?attention=1` (the daily-digest notification)
    // switches Needs attention ON. Its own param, never a ?filter= value, so
    // every existing filter link keeps working and the two can combine. Applied
    // once, then removed from the URL (replace — no extra Back step): the
    // toggle stays "not persisted" (Seam 8), and tapping the digest again while
    // already here still works, because the URL changes again.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('attention') !== '1') return;
        setAttentionOnly(true);
        params.delete('attention');
        const rest = params.toString();
        navigate({ pathname: location.pathname, search: rest ? `?${rest}` : '' }, { replace: true });
    }, [location.search, location.pathname, navigate]);

    // qexp-6f2a9d38-list — how many days out an estimate starts reading
    // "Expiring soon". Org setting, falls back to 4.
    const expiryWarningDays = resolveExpiryWarningDays(companySettings?.quoteDefaults);

    // qattn-8c4e1f92-list — the stage list is what carries staleAfterDays. Null
    // until the first snapshot lands, which the attention rule treats as "say
    // nothing about the stage-keyed rungs" rather than guessing.
    const pipelineStages = usePipelineStages(currentUserProfile?.organizationId);

    // qview-6b2d4e91 — Seam 10. ONE listener on the org's view records, attached
    // to each row below exactly like `attention`, so the table, the phone card
    // and the board read the same object and the board opens no query of its own.
    const viewsById = useQuoteViews(currentUserProfile?.organizationId);

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const quotesPath = `organizations/${currentUserProfile.organizationId}/quotes`;
        const q = query(collection(db, quotesPath), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allQuotes = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    createdAt: data.createdAt?.toDate(),
                    expiresAt: data.expiresAt?.toDate(),
                    // Ensure status is a string to prevent errors
                    status: data.status || 'Draft'
                };
            });

            // --- SMART FILTER: SECURITY LOGIC ---
            // 1. Office Staff + every high-level role: See EVERYTHING (officecust-9a4d2c61 — founder
            //    decision 2026-10-04; the old inline list left out Office Staff and the plain
            //    'Owner' / 'System Admin' spellings)
            // 2. Sales Consultants: See ONLY quotes they created
            const isHighLevel = canSeeAllCustomers(currentUserProfile);

            const visibleQuotes = allQuotes.filter(quote => {
                if (isHighLevel) return true;
                // Seam 0: un-hide legacy quotes that were NEVER attributed
                // (createdBy undefined — pre-provenance editor-created). Strict
                // undefined check only: an explicit null is an ORPHAN (was
                // attributed, the user was later deleted) and stays scoped as
                // today, i.e. NOT shown to a non-high-level user.
                if (quote.createdBy === undefined) return true;
                // Strict check: Did I create this quote?
                return quote.createdBy === currentUserProfile.id;
            });

            setQuotes(visibleQuotes.filter(q => !isQuoteArchived(q))); // estdel-f9ac8ba8
            setDeletedQuotes(visibleQuotes.filter(q => isQuoteArchived(q)));
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching quotes:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [currentUserProfile]);

    const sortedQuotes = useMemo(() => {
        // qexp-6f2a9d38-list — Seam 5. expiresAt was ALREADY being read into row
        // state here and then thrown away, so the derived state costs no extra
        // query and no extra read. Attached once per row so the filter below and
        // the badge in the table can't disagree.
        // qattn-8c4e1f92-list — Seam 8. Attached in the SAME map as expiryState,
        // for the same reason: every field the rule reads is already on the row,
        // so this costs no extra query and no extra read, and the chip, the
        // filter and the estimate page cannot disagree about one estimate.
        let processed = quotes.map(q => {
            const stage = pipelineStages ? findPipelineStage(q.pipelineStageId, pipelineStages) : null;
            return {
                ...q,
                expiryState: resolveExpiryState(q, {
                    timeZone: companySettings?.timezone,
                    warningDays: expiryWarningDays,
                }).state,
                attention: resolveQuoteAttention(q, stage, {
                    timeZone: companySettings?.timezone,
                }),
                // qview-6b2d4e91 — null when the customer has never viewed it.
                viewSummary: quoteViewSummary(viewsById.get(q.id)),
            };
        });

        // --- DEBUG LOG ---
        if (filterParam) {
            console.log("Applying Quote Filter:", filterParam);
        }

        // --- 1. FILTER LOGIC (Case Insensitive) ---
        if (filterParam) {
            const filter = filterParam.toLowerCase();
            
            if (filter === 'approved') {
                // CONVERT-FIX: exclude converted quotes so this list matches
                // the dashboard's "Approved" tile (both mean "ready to convert").
                processed = processed.filter(q =>
                    (q.status.toLowerCase() === 'approved' ||
                    q.status.toLowerCase() === 'accepted') &&
                    !q.convertedToJobId
                );
            } else if (filter === 'converted') {
                // Conversion is a marker (convertedToJobId), not a status — Choice A.
                processed = processed.filter(q => !!q.convertedToJobId);
            } else if (filter === 'draft') {
                processed = processed.filter(q => q.status.toLowerCase() === 'draft');
            } else if (filter === 'pending') {
                processed = processed.filter(q =>
                    q.status.toLowerCase() === 'sent' ||
                    q.status.toLowerCase() === 'changes requested'
                );
            } else if (filter === 'expired') {
                // qexp-6f2a9d38-list — DERIVED, never a stored status. The old
                // mobile chip compared against status 'Expired', which nothing has
                // ever written, so it matched nothing forever.
                processed = processed.filter(q => q.expiryState === EXPIRY_STATES.EXPIRED);
            } else if (filter === 'expiringsoon') {
                processed = processed.filter(q => q.expiryState === EXPIRY_STATES.EXPIRING_SOON);
            }
        }

        // qattn-8c4e1f92-list — Seam 8. Applied AFTER the status filter, never
        // inside the chain: attention is its own axis, so "Draft + needs
        // attention" has to be expressible. Mirrors the needsQbAttention AND on
        // InvoicesView.
        if (attentionOnly) {
            processed = processed.filter(q => q.attention?.level === 'attention');
        }

        // qboard-7e3c9b42 — salesperson, on its own axis like the attention
        // toggle. resolveQuoteSalesPerson is the existing read-side precedence
        // (salesPersonId → sales_person_id → createdBy), not a new rule.
        if (salesFilter !== SALES_FILTER_ALL) {
            processed = processed.filter(q => {
                const id = resolveQuoteSalesPerson(q);
                return salesFilter === SALES_FILTER_UNASSIGNED ? !id : id === salesFilter;
            });
        }

        // qboard-7e3c9b42 — search, reusing Seam 7b's matcher (number, title,
        // customer). It expects a lowercased term, as CalendarView does.
        const needle = searchTerm.trim().toLowerCase();
        if (needle) {
            processed = processed.filter(q => matchesQuoteSearch(q, needle));
        }

        // --- 2. SORT LOGIC ---
        if (sortConfig.key) {
            processed.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return processed;
    }, [quotes, sortConfig, filterParam, companySettings?.timezone, expiryWarningDays, pipelineStages, attentionOnly, salesFilter, searchTerm, viewsById]); // Re-run when filterParam changes

    // qboard-7e3c9b42 — only salespeople who actually appear on an estimate get
    // an option, so the dropdown doesn't list every employee in the org. Names
    // come from the users array; an id we can't resolve still gets an option
    // (labelled Unknown) rather than silently dropping its estimates.
    const salesOptions = useMemo(() => {
        const nameById = new Map((users || []).map(u => [u.id, u.name || u.email || null]));
        const seen = new Map();
        let anyUnassigned = false;
        for (const q of quotes) {
            const id = resolveQuoteSalesPerson(q);
            if (!id) { anyUnassigned = true; continue; }
            if (!seen.has(id)) seen.set(id, nameById.get(id) || 'Unknown');
        }
        const list = [...seen.entries()]
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
        if (anyUnassigned) list.push({ id: SALES_FILTER_UNASSIGNED, name: 'Unassigned' });
        return list;
    }, [quotes, users]);

    // qattn-8c4e1f92-list — how many estimates need a human, across the whole
    // list. Computed independently of attentionOnly so the number on the toggle
    // does not change to "N of N" the moment it is switched on.
    const attentionCount = useMemo(() => {
        if (!pipelineStages) return 0;
        return quotes.reduce((n, q) => (
            resolveQuoteAttention(q, findPipelineStage(q.pipelineStageId, pipelineStages), {
                timeZone: companySettings?.timezone,
            }).level === 'attention' ? n + 1 : n
        ), 0);
    }, [quotes, pipelineStages, companySettings?.timezone]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' 
            ? <ArrowUp className="inline ml-1 h-4 w-4" /> 
            : <ArrowDown className="inline ml-1 h-4 w-4" />;
    };

    const clearFilter = () => {
        navigate('/quotes'); // Remove URL param
    };

    // r1-p1-docterm-qv-term-5f8c2b41 — org-default terminology for every aggregate label on this screen
    const term = resolveDocTerminology(companySettings?.quoteTerminologyDefault);

    if (isLoading) return <p className="text-center mt-8">Loading {term.wordPlural.toLowerCase()}...</p>;

    return (
        <div className="p-4 sm:p-6 md:p-10 pb-24">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">{term.wordPlural}</h1>{/* r1-p1-docterm-qv-title-9a4d6e23 */}
                    {filterParam && (
                        <div className="flex items-center gap-2 mt-2">
                            <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded border border-blue-200">
                                Filter: {filterParam}
                            </span>
                            <button onClick={clearFilter} className="text-gray-400 hover:text-red-500 transition-colors">
                                <XCircle size={16} />
                            </button>
                        </div>
                    )}
                </div>
                <div className="w-full sm:w-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    {/* qattn-8c4e1f92-list — Seam 8. Same treatment as the
                        "Needs QB attention" chip on Invoices: amber-500 filled
                        when active, gray-bordered when not. Hidden entirely when
                        nothing needs attention, so a tidy list stays tidy. */}
                    {/* qnotif-6d2f8b47 — also shown while ON, so arriving from the
                        digest after everything was dealt with (count 0) still
                        leaves a way to switch it off. */}
                    {(attentionCount > 0 || attentionOnly) && (
                        <button
                            onClick={() => setAttentionOnly(v => !v)}
                            title={attentionOnly ? 'Show all' : 'Show only the ones that need you'}
                            className={`flex justify-center items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${
                                attentionOnly
                                    ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <AlertTriangle size={14} />
                            Needs attention
                            <span className={`ml-0.5 text-xs font-bold ${attentionOnly ? 'text-white' : 'text-amber-600'}`}>
                                {attentionCount}
                            </span>
                        </button>
                    )}
                    {/* estdel-f9ac8ba8 — the Deleted estimates view. Same chip treatment as Needs attention; shown
                        only when there is something deleted (or while it is open, so it can be closed). */}
                    {(deletedQuotes.length > 0 || showDeleted) && (
                        <button
                            onClick={() => setShowDeleted(v => !v)}
                            title={showDeleted ? `Back to all ${term.wordPlural.toLowerCase()}` : `Deleted ${term.wordPlural.toLowerCase()}`}
                            aria-pressed={showDeleted}
                            className={`flex justify-center items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${
                                showDeleted
                                    ? 'bg-gray-700 text-white border-gray-700 hover:bg-gray-800'
                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <EyeOff size={14} />
                            Deleted ({deletedQuotes.length})
                        </button>
                    )}
                    {/* qboard-7e3c9b42 — board / list, same icon pair and same
                        active styling as the customer board's toggle. */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setViewMode('board')}
                            title="Board"
                            aria-label="Board view"
                            aria-pressed={viewMode === 'board'}
                            className={`p-2 rounded-md transition-colors ${viewMode === 'board' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                        >
                            <LayoutGrid size={20} />
                        </button>
                        <button
                            onClick={() => setViewMode('table')}
                            title="List"
                            aria-label="List view"
                            aria-pressed={viewMode === 'table'}
                            className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                        >
                            <List size={20} />
                        </button>
                    </div>
                    <button
                        onClick={() => openQuoteModal()}
                        className="w-full sm:w-auto flex justify-center items-center p-3 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 shadow-sm"
                    >
                        <Plus size={20} className="mr-2" /> Create {term.word}{/* r1-p1-docterm-qv-btn-3c7f1b58 */}
                    </button>
                </div>
            </div>

            {/* qboard-7e3c9b42 — search + salesperson, applied to BOTH views so
                switching the toggle never changes which estimates you are
                looking at. */}
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <div className="relative flex-1 max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={`Search by number, title or customer`}
                        className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-md bg-white"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label="Clear search"
                        >
                            <XCircle size={16} />
                        </button>
                    )}
                </div>
                {salesOptions.length > 0 && (
                    <select
                        value={salesFilter}
                        onChange={(e) => setSalesFilter(e.target.value)}
                        aria-label="Filter by salesperson"
                        className="py-2 px-3 text-sm border border-gray-300 rounded-md bg-white"
                    >
                        <option value={SALES_FILTER_ALL}>All salespeople</option>
                        {salesOptions.map(o => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                    </select>
                )}
            </div>

            {/* qboard-7e3c9b42 — the board. Handed the SAME filtered+sorted array
                the table below renders, so the two views can never disagree
                about which estimates exist, and no second listener is opened. */}
            {/* estdel-f9ac8ba8 — the Deleted view replaces the list / board while it is open; search still applies */}
            {showDeleted && (
                <DeletedQuotesTable
                    quotes={searchTerm.trim() ? deletedQuotes.filter(q => matchesQuoteSearch(q, searchTerm.trim().toLowerCase())) : deletedQuotes}
                    currentUserProfile={currentUserProfile}
                    onQuoteSelect={onQuoteSelect}
                    term={term}
                />
            )}

            {viewMode === 'board' && !showDeleted && (
                <QuotePipelineBoard
                    orgId={currentUserProfile?.organizationId}
                    stages={pipelineStages}
                    quotes={sortedQuotes}
                    users={users}
                    currentUserProfile={currentUserProfile}
                    companySettings={companySettings}
                    term={term}
                    onQuoteSelect={onQuoteSelect}
                />
            )}

            {/* --- DESKTOP VIEW (Table) --- */}
            <div className={`${viewMode === 'board' || showDeleted ? 'hidden' : 'hidden md:block'} bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200`}>
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                        <tr>
                            <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('quoteNumber')}>{term.word} #{getSortIcon('quoteNumber')}</th>{/* r1-p1-docterm-qv-col-6e2a8d74 */}
                            <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('customerName')}>Customer{getSortIcon('customerName')}</th>
                            <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('createdAt')}>Date{getSortIcon('createdAt')}</th>
                            <th className="px-6 py-3 cursor-pointer hover:bg-gray-100 text-right" onClick={() => requestSort('total')}>Total{getSortIcon('total')}</th>
                            <th className="px-6 py-3 cursor-pointer hover:bg-gray-100 text-center" onClick={() => requestSort('status')}>Status{getSortIcon('status')}</th>
                            {/* qview-6b2d4e91 — Seam 10. Not sortable: the row's sort reads a
                                top-level field, and this is derived. */}
                            <th className="px-6 py-3">Viewed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedQuotes.map(quote => (
                            <tr key={quote.id} onClick={() => onQuoteSelect(quote)} className="bg-white border-b hover:bg-blue-50 cursor-pointer transition-colors">
                                <td className="px-6 py-4 font-medium text-gray-900">{quote.quoteNumber}</td>
                                <td className="px-6 py-4">{quote.customerName}</td>
                                <td className="px-6 py-4">{formatDate(quote.createdAt)}</td>
                                <td className="px-6 py-4 text-right font-semibold text-gray-900">{formatCurrency(quote.total)}</td>
                                {/* r1-dep-qv-badge-8c2f5a91 — depositStatus composes the deposit states into the badge */}
                                <td className="px-6 py-4 text-center">
                                    <QuoteStatusBadge status={quote.status} depositStatus={quote.depositStatus} isConverted={!!quote.convertedToJobId} expiryState={quote.expiryState} isPartialApproval={quote.status?.toLowerCase() === 'approved' && Array.isArray(quote.approvedItemIds) && Array.isArray(quote.declinedItemIds) && quote.approvedItemIds.length > 0 && quote.declinedItemIds.length > 0} />
                                    {/* depdraft-3c8e5a14 — "Collect a deposit" ON with no amount yet */}
                                    {isDepositAmountNeeded(quote) && (
                                        <div className="mt-1 text-[11px] font-semibold text-amber-700">Deposit amount needed</div>
                                    )}
                                    {/* qattn-8c4e1f92-list — a SECOND chip, under the status, never
                                        replacing it. Status says where the estimate is; this says
                                        whether it needs a person. Expiry stays on the status badge
                                        and is deliberately not repeated here. */}
                                    {quote.attention?.level === 'attention' && (
                                        <div className="mt-1 flex justify-center">
                                            <span title={quote.attention.reasons.join(' ')}
                                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                                <AlertTriangle size={11} />
                                                {quoteAttentionLabel(quote.attention)}
                                            </span>
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-xs whitespace-nowrap">
                                    {quote.viewSummary
                                        ? <span title={quote.viewSummary.full} className="text-gray-700">{quote.viewSummary.short}{quote.viewSummary.count > 1 ? ` · ${quote.viewSummary.count}×` : ''}</span>
                                        : <span className="text-gray-300">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- MOBILE VIEW (Cards) --- */}
            <div className={`${viewMode === 'board' || showDeleted ? 'hidden' : 'md:hidden'} space-y-3`}>
                {sortedQuotes.map(quote => (
                    <div 
                        key={quote.id} 
                        onClick={() => onQuoteSelect(quote)}
                        className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 active:scale-[0.99] transition-transform cursor-pointer relative"
                    >
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">#{quote.quoteNumber}</span>
                                <h3 className="text-lg font-bold text-gray-900">{quote.customerName || 'Unknown Customer'}</h3>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                                <QuoteStatusBadge status={quote.status} depositStatus={quote.depositStatus} isConverted={!!quote.convertedToJobId} expiryState={quote.expiryState} isPartialApproval={quote.status?.toLowerCase() === 'approved' && Array.isArray(quote.approvedItemIds) && Array.isArray(quote.declinedItemIds) && quote.approvedItemIds.length > 0 && quote.declinedItemIds.length > 0} />
                                {/* depdraft-3c8e5a14 — "Collect a deposit" ON with no amount yet */}
                                {isDepositAmountNeeded(quote) && <span className="text-[11px] font-semibold text-amber-700">Deposit amount needed</span>}
                            </div>
                        </div>

                        {/* qattn-8c4e1f92-list — on a phone card there is room for the
                            actual sentence, so show the primary reason rather than
                            the one-word chip. */}
                        {quote.attention?.level === 'attention' && (
                            <div className="mb-2 flex items-start gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                                <AlertTriangle size={13} className="mt-px shrink-0" />
                                <span>{quote.attention.reasons[0]}</span>
                            </div>
                        )}

                        {/* qview-6b2d4e91 — Seam 10 */}
                        {quote.viewSummary && (
                            <p className="mb-1 flex items-center gap-1.5 text-xs text-gray-500">
                                <Eye size={12} className="shrink-0" />
                                {quote.viewSummary.short}{quote.viewSummary.count > 1 ? ` · ${quote.viewSummary.count} views` : ''}
                            </p>
                        )}

                        <div className="flex justify-between items-end border-t border-gray-100 pt-3 mt-2">
                            <div className="text-sm text-gray-500">
                                {formatDate(quote.createdAt)}
                            </div>
                            <div className="text-xl font-bold text-blue-600">
                                {formatCurrency(quote.total)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* qboard-7e3c9b42 — the board carries its own empty state (it still
                draws its columns when nothing matches), so this one is for the
                list only. searchTerm/salesFilter are named here because "no
                estimates yet" is a lie when a filter is what emptied the list. */}
            {viewMode !== 'board' && !showDeleted && sortedQuotes.length === 0 && (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300 mt-4">
                    <p className="text-gray-500">
                        {filterParam ? `No ${term.wordPlural.toLowerCase()} match "${filterParam}"`
                            : (searchTerm.trim() || salesFilter !== SALES_FILTER_ALL || attentionOnly)
                                ? `No ${term.wordPlural.toLowerCase()} match the current filters.`
                                : `No ${term.wordPlural.toLowerCase()} yet.`}
                    </p>{/* r1-p1-docterm-qv-empty-1f5b9c37 */}
                </div>
            )}
        </div>
    );
};

export default QuotesView;